import { includes, intersection } from 'lodash';
import type { ComponentType, FC, ReactElement } from 'react';
import { Suspense } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, Navigate } from 'react-router-dom';

import { Stack } from '@mui/material';

import type { RootState } from 'store';

import { useDCRAccess } from 'common/dcr/utils/useDCRAccess';
import { RoleVariantEnum, UserTypes } from 'common/graphql/types';
import { useIsClinicalTrialLeadUser } from 'common/hooks/useIsClinicalTrialLeadUser';
import { OfflineManager } from 'common/native-app-support/native.offline.manager';
import type { RouteType } from 'common/types/route';
import { ECONSENT_REGEX, STUDY_ID_REGEX, STUDY_VERSION_URL_REGEX } from 'common/utils/constants';
import { Loading, SuspenseFallback } from 'common/utils/loading';

import { isUserLoggedIn } from './auth_role_permissions_utils';
import StudyRoute from './auth_study_restrict';
import useStudySiteQueryParamUpdate from './hooks/useStudySiteQueryParamUpdate';

interface AccessibleRouteProps {
    component: ComponentType<any>;
}

const AccessibleRoute = ({ component: Component, ...props }: AccessibleRouteProps): ReactElement => {
    const location = useLocation();
    const checkStudyDeleted =
        !STUDY_VERSION_URL_REGEX.test(location.pathname) && STUDY_ID_REGEX.test(location.pathname);

    if (checkStudyDeleted && !ECONSENT_REGEX.test(location.pathname)) {
        return <StudyRoute component={Component} studyId={location.pathname.match(STUDY_ID_REGEX)[0]} {...props} />;
    }

    return (
        <Suspense fallback={<SuspenseFallback />}>
            <Component {...props} />
        </Suspense>
    );
};

const hasPermission = (userPermissions, requiredPermissions): boolean =>
    !!(
        !requiredPermissions ||
        !requiredPermissions.length ||
        intersection(requiredPermissions, userPermissions).length
    );

const hasRole = (userRole, requiredRole): boolean => includes(requiredRole, userRole);

interface Props {
    component: ComponentType<any>;
    role: string[];
    permissions: string[];
    permsRedirect?: string;
    accessRoute?: RouteType;
}

export const AuthenticatedRoute: FC<Props> = ({
    component,
    role = [],
    permissions = [],
    permsRedirect = '/',
    accessRoute,
}: Props): ReactElement => {
    const auth = useSelector((state: RootState) => state.auth);
    const { loading } = useStudySiteQueryParamUpdate(auth?.activeUser);

    const { canAccessDCR, isLoading: isDCRLoading } = useDCRAccess();
    const isClinicalTrialLeadUser = useIsClinicalTrialLeadUser();
    const location = useLocation();

    const getComponentOrRedirect = (accessRoute: RouteType): ReactElement => {
        const isLoggedIn = isUserLoggedIn(auth);

        const roleId =
            auth.activeUser?.roleVClaim?.sk ||
            // Since base role ID of sysadmin and subadmin are the same, differentiate them with role attribute
            (auth.activeUser?.roleBClaim?.sk && auth.activeUser?.role === RoleVariantEnum.SystemAdministrator
                ? RoleVariantEnum.SystemAdministrator
                : auth.activeUser?.roleBClaim?.sk) ||
            null;

        let hasRequiredPerms = isLoggedIn ? hasRole(roleId, role) : false;
        hasRequiredPerms =
            isLoggedIn && !hasRequiredPerms && !role
                ? hasPermission(auth.activeUser.permissions, permissions)
                : hasRequiredPerms;

        if (isLoggedIn && hasRequiredPerms) {
            if (location.pathname === '/dcrDashboard') {
                if (isDCRLoading) {
                    return (
                        <Stack sx={{ position: 'fixed', inset: 0, alignItems: 'center', justifyContent: 'center' }}>
                            <Loading isLoading={isDCRLoading} />
                        </Stack>
                    );
                }
                if (!canAccessDCR && !isClinicalTrialLeadUser) {
                    return (
                        <Navigate
                            to={isClinicalTrialLeadUser ? '/study' : permsRedirect}
                            state={{ target: location.pathname }}
                            replace
                        />
                    );
                }
            }

            if (
                !OfflineManager.getInstance().isPhoneOffline() && // Privacy policy page opening delayed until user login online
                auth.activeUser.currentPolicy?.[0] &&
                auth.activeUser.acceptedVersionPP?.toString() !==
                    auth.activeUser.currentPolicy[0].majorVersion.toString() &&
                location.pathname !== '/policy' &&
                !auth?.switchingUser // Don't redirect if switch user modal is opened, we will open policy inside modal over there
            ) {
                return <Navigate to="/policy" state={{ target: location.pathname }} replace />;
            }

            const studySiteReady = auth?.activeStudySite?.activeStudySiteReady;
            const studyId = auth?.activeStudySite.activeStudy?.studyId;
            const siteId = auth?.activeStudySite.activeSite?.siteId;

            if (
                (!!accessRoute.studySiteRequired && (!studySiteReady || !studyId || !siteId)) ||
                (accessRoute.studyOnlyRequired && (!studySiteReady || !studyId))
            ) {
                if (loading) {
                    return (
                        <Stack sx={{ position: 'fixed', inset: 0, alignItems: 'center', justifyContent: 'center' }}>
                            <Loading isLoading />
                        </Stack>
                    );
                }

                return <Navigate to="/activestudysite" state={{ target: location.pathname }} replace />;
            }

            return <AccessibleRoute component={component} />;
        }

        if (!isLoggedIn) {
            localStorage.setItem('postLoginRedirectUrl', location.pathname + location.search);

            return <Navigate to="/" state={{ target: location.pathname }} replace />;
        }

        if (auth.activeUser.userType === UserTypes.Patient) {
            return <Navigate to="/patientAccessDenied" state={{ target: location.pathname }} replace />;
        }
        if (roleId === RoleVariantEnum.Librarian) {
            return <Navigate to="/activitylibrary" state={{ target: location.pathname }} replace />;
        }
        if (roleId === RoleVariantEnum.StudyBuilder) {
            return <Navigate to="/study" state={{ target: location.pathname }} replace />;
        }

        return <Navigate to={permsRedirect} state={{ target: location.pathname }} replace />;
    };

    return getComponentOrRedirect(accessRoute);
};
